import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import useSWR from "swr";
import {
  PartyPopper, Sparkles, ArrowRight, Star, CalendarCheck,
  Stethoscope, GraduationCap, Siren, Shield,
} from "lucide-react";
import { api } from "@/lib/api";
import Particles from "@/components/Particles";
import Countdown from "@/components/Countdown";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import GiftCardPurchase from "@/components/GiftCardPurchase";
import { useContent } from "@/context/ContentContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

/* ---------------- HERO: Coming Soon / Now Open ---------------- */
function Hero({ content }) {
  const hero = content?.hero || {};
  const opening = content?.opening_date || "2026-09-11T10:00:00-05:00";
  const [isOpen, setIsOpen] = useState(Date.now() >= new Date(opening).getTime());

  return (
    <section className="section-dark relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero">
      <div className="aurora w-[520px] h-[520px] -top-24 left-1/4" style={{ background: "#f6b64a", opacity: 0.3 }} />
      <div className="aurora w-[440px] h-[440px] top-1/4 right-10" style={{ background: "#ff5147", opacity: 0.3 }} />
      <div className="retro-grid" />
      <div className="absolute inset-0 z-[2]"><Particles density={60} /></div>

      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 glass-dark rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#2fd0c6] mb-6">
            <CalendarCheck size={13} /> {hero.eyebrow || "Keys / Park Hill, Oklahoma"}
          </span>

          <h1 className="font-display text-6xl sm:text-8xl font-black leading-none">
            {isOpen ? <span className="gradient-text">NOW OPEN</span> : <span className="neon-text animate-flicker">COMING SOON</span>}
          </h1>
          <p className="mt-6 text-base sm:text-xl text-white/75 max-w-xl mx-auto leading-relaxed">
            {isOpen ? "The doors are open! Book the entire facility for your next celebration." : hero.subheadline}
          </p>

          <div className="mt-10 flex flex-col items-center gap-3">
            {!isOpen && <p className="text-xs uppercase tracking-[0.22em] text-white/50">Grand Opening, {new Date(opening).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>}
            <Countdown target={opening} onOpen={() => setIsOpen(true)} />
          </div>

          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <Link to="/reservations" className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-base" data-testid="hero-reserve-btn">
              <PartyPopper size={20} /> {hero.primary_cta || "Reserve Your Party"}
            </Link>
            <Link to="/whats-included" className="btn-ghost-light inline-flex items-center gap-2 px-8 py-4 text-base" data-testid="hero-included-btn">
              <Sparkles size={18} /> {hero.secondary_cta || "See What's Included"}
            </Link>
          </div>
        </motion.div>
      </div>
      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[#120d09] to-transparent z-[3]" />
    </section>
  );
}

function StatCounter({ end, label, prefix = "", suffix = "" }) {
  const [val, setVal] = React.useState(0);
  const ref = React.useRef(null);
  const started = React.useRef(false);
  React.useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const dur = 1300, start = performance.now();
        const tick = (t) => { const p = Math.min(1, (t - start) / dur); setVal(Math.floor(p * end)); if (p < 1) requestAnimationFrame(tick); };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end]);
  return (
    <div ref={ref} className="text-center">
      <div className="font-mono-arcade text-4xl sm:text-5xl font-extrabold text-glow-cyan">{prefix}{val}{suffix}</div>
      <div className="text-sm text-white/55 mt-2">{label}</div>
    </div>
  );
}

function HowItWorks({ steps }) {
  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading eyebrow="Easy As 1, 2, 3, 4" title="How It Works" subtitle="Booking your private party takes just a few minutes." dark />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(steps || []).map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="card card-lift p-7 relative overflow-hidden">
              <div className="font-mono-arcade text-6xl font-extrabold text-white/5 absolute top-2 right-4">{i + 1}</div>
              <div className="relative">
                <div className="h-11 w-11 grid place-items-center rounded-xl bg-[#ff3b3b]/15 text-[#ff3b3b] font-mono-arcade font-bold mb-4">{i + 1}</div>
                <h3 className="font-display text-lg font-bold mb-2 text-white">{s.title}</h3>
                <p className="text-sm text-white/60">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const DISCOUNT_ICONS = { Healthcare: Stethoscope, Education: GraduationCap, "First Responders": Siren, Military: Shield };
function HeroDiscount({ discount }) {
  if (!discount) return null;
  return (
    <section className="py-20 surface-alt">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="relative overflow-hidden rounded-[2rem] card p-8 sm:p-12" style={{ borderColor: "rgba(255,210,62,0.4)" }} data-testid="hero-discount-section">
          <div className="aurora w-72 h-72 -top-16 -right-10" style={{ background: "#ffd23e", opacity: 0.2 }} />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#ffd23e] mb-3"><Star size={14} /> Exclusive Offer</span>
              <h2 className="font-display text-3xl sm:text-5xl font-black text-white">${discount.amount} Off, the <span className="gradient-text">{discount.title}</span></h2>
              <p className="mt-4 text-white/70">{discount.description}</p>
              <p className="mt-3 text-xs text-white/45">{discount.terms}</p>
              <Link to="/reservations" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 mt-6" data-testid="discount-claim-btn">Claim Your Discount <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {discount.eligible.map((g) => {
                const Icon = DISCOUNT_ICONS[g] || Star;
                return (<div key={g} className="card card-lift p-5 text-center"><Icon className="mx-auto text-[#21e6cf] mb-2" size={28} /><span className="text-sm font-semibold text-white">{g}</span></div>);
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Reviews({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading eyebrow="Loved By Families" title="What Guests Are Saying" dark />
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="card card-lift p-7">
              <div className="flex gap-1 mb-4">{[...Array(5)].map((_, s) => <Star key={s} size={16} className="fill-[#ffd23e] text-[#ffd23e]" />)}</div>
              <p className="text-white/80 leading-relaxed mb-5">"{r.text}"</p>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full grid place-items-center font-bold text-white" style={{ background: "linear-gradient(120deg,#ff5147,#2fd0c6)" }}>{r.name?.[0]}</div>
                <div><p className="font-semibold text-sm text-white">{r.name}</p><p className="text-xs text-white/45">{r.role}</p></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryPreview() {
  const { data: images } = useSWR("/gallery", fetcher);
  const list = (images || []).slice(0, 6);
  return (
    <section className="py-24 surface-alt">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading eyebrow="Peek Inside" title="The Fun Awaits" dark />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {list.map((img, i) => (
            <motion.div key={img.id || i} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
              className={`overflow-hidden rounded-2xl border border-white/10 group ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
              <img src={img.url} alt={img.caption} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10"><Link to="/gallery" className="btn-outline inline-flex items-center gap-2 px-7 py-3.5">View Full Gallery <ArrowRight size={16} /></Link></div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24">
      <div className="mx-auto max-w-5xl px-6">
        <div className="relative overflow-hidden rounded-[2.5rem] card p-10 sm:p-16 text-center">
          <Particles density={26} className="absolute inset-0 opacity-70" />
          <div className="relative z-10">
            <h2 className="font-display text-4xl sm:text-6xl font-black text-white">Ready to <span className="gradient-text">Celebrate?</span></h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">Reserve the entire facility for your next big moment. A two hour minimum, and memories that last forever.</p>
            <Link to="/reservations" className="btn-primary inline-flex items-center gap-2 px-10 py-4 text-lg mt-8" data-testid="final-cta-btn"><PartyPopper size={22} /> Reserve Your Party</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function GiftCardSection() {
  return (
    <section className="py-24 surface-alt" id="gift-cards">
      <div className="mx-auto max-w-5xl px-6">
        <SectionHeading eyebrow="Give The Gift Of Fun" title="Keys Gift Cards" subtitle="The perfect present for birthdays and holidays. Redeemable toward any reservation." dark />
        <GiftCardPurchase />
      </div>
    </section>
  );
}

export default function Home() {
  const { content } = useContent();
  return (
    <div>
      <Seo title="Keys Event Center | Private Family Fun in Park Hill, Oklahoma" description="Book the whole facility for birthdays, reunions and more at Keys Event Center. Now booking private parties in Keys / Park Hill, OK." />
      <Hero content={content} />
      <section className="py-16 surface-alt border-y border-white/10">
        <div className="mx-auto max-w-4xl px-6 grid grid-cols-3 gap-8">
          <StatCounter end={100} suffix="%" label="Private Bookings" />
          <StatCounter end={11} suffix="+" label="Attractions Included" />
          <StatCounter end={50} prefix="$" label="HERO Discount" />
        </div>
      </section>
      <HowItWorks steps={content?.homepage?.how_it_works} />
      <HeroDiscount discount={content?.discount} />
      <GalleryPreview />
      <Reviews items={content?.reviews?.items} />
      {content?.settings?.gift_cards_enabled && <GiftCardSection />}
      <FinalCTA />
    </div>
  );
}
